import {ClientOptions, ProfileKey, sapi, SapiClient} from '@findhotel/sapi'

// TODO: browser specific code should be placed in the client or in core/_web folder
import {isFlutterAppWebView} from '../../_web/utils/flutter/isFlutterAppWebView'

const SAPI_CLIENT_TIMEOUT = 1500
const SAPI_CLIENT_POLL = 50

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

let clientPromise: Promise<SapiClient>
/**
 * Key used to reinitialize the clientPromise if the parameters change
 */
let clientCacheKey: string
const FIXED_KEY = 'FIXED_KEY'

// These keys are not sensitive and they are only used for the backend to distinguish where the request is coming from
const APP_API_KEY = 'vio_app_1a2b3c4d5e6f7g8h9i0j1k2l3m4n5o6p'
export const WEB_API_KEY = 'vio_website_8f7d6e5c4b3a2d1e0f9c8b7a6d5e4f3c'

/**
 * Initialize a SAPI client and store it in the module scope to be retrieved asynchronously (potentially outside react)
 * SAPI SDK expects to only have one client per app, so we use this module to act as a singleton
 * @param profileKey - Profile key for customer identification
 * @param options - Options for initializing SAPI client
 * @param allowResetInstance - Boolean to allow SAPI singleton to be reset if the options change. Default: `false`
 * @param onReset - Callback that will be called whenever the client is reset, use this to reset your existing state if needed
 * @returns Promise which resolves to SAPI client
 */
export const initSapiClient = async (
  profileKey: ProfileKey,
  options: ClientOptions,
  allowResetInstance = false,
  onReset?: () => void
): Promise<SapiClient> => {
  // We cannot rely on singletons in server environments
  if (typeof window === 'undefined')
    throw new Error(
      'initSapiClient should only be used in the browser, use `sapi` directly in server environments'
    )

  const key = allowResetInstance
    ? JSON.stringify(options)
    : clientCacheKey || FIXED_KEY // If another consumer DOES use reset, reuse it to avoid flipping between keys at every render (= infinite loop)

  if (clientPromise && clientCacheKey === key) return clientPromise
  const hadExistingClient = !!clientCacheKey

  const apiKey = isFlutterAppWebView() ? APP_API_KEY : WEB_API_KEY

  clientPromise = sapi(profileKey, {
    ...options,
    apiKey
  })
  clientCacheKey = key

  if (hadExistingClient) onReset?.()

  return clientPromise
}

/**
 * Get the SAPI client that was initialized asynchronously
 */
export const getSapiClient = async (): typeof clientPromise => {
  // Is initializing / already initialized
  if (clientPromise) return clientPromise

  // Prevent silent hang by timing out, this can happen when you forget to initialize SAPI (eg. sc-92616)
  const timeout = setTimeout(() => {
    throw new Error('get SAPI client timeout')
  }, SAPI_CLIENT_TIMEOUT)

  // Poll for promise of client
  while (!clientPromise) await sleep(SAPI_CLIENT_POLL)
  clearTimeout(timeout)
  return clientPromise
}
